import React, { useCallback, useMemo, useState } from 'react';
import { Button, Space, Tooltip } from 'antd';
import { FolderOpenOutlined, UploadOutlined } from '@ant-design/icons';
import UploadDocumentModal from '../../../components/documents/UploadDocumentModal';
import DocumentPreviewModal from '../../../components/documents/DocumentPreviewModal';
import type { Application } from '../../../types';
import { useGetClosingPackageCompositionQuery } from '../api/closingPackageApi';
import { useCompositionActionHandlers } from '../hooks/useCompositionActionHandlers';
import { useCompositionEditor } from '../hooks/useCompositionEditor';
import { useClosingPackagePermissions } from '../hooks/useClosingPackagePermissions';
import type { ClosingPackageProjection } from '../types';
import CompositionEditBanner from './composition/CompositionEditBanner';
import CompositionExcludeConfirmModal from './composition/CompositionExcludeConfirmModal';
import CompositionHeader, {
  type CompositionHeaderCommand,
} from './composition/CompositionHeader';
import CompositionInitializeConfirmModal from './composition/CompositionInitializeConfirmModal';
import CompositionTable from './composition/CompositionTable';
import CompositionToolbar, {
  type CompositionToolbarDocumentActions,
} from './composition/CompositionToolbar';
import DocumentPickerDrawer from './documentPicker/DocumentPickerDrawer';
import './closingPackageWorkspace.css';

const UPLOAD_PERMISSION_TOOLTIP = 'Нет права загрузки документов';

export type ClosingPackageCompositionEditorProps = {
  projection: ClosingPackageProjection;
};

/**
 * FE-005A-7 — Initialize UX, source/manual badges, Composition Editor finalization.
 */
const ClosingPackageCompositionEditor: React.FC<ClosingPackageCompositionEditorProps> = ({
  projection,
}) => {
  const { data: composition, isFetching: compositionLoading } =
    useGetClosingPackageCompositionQuery(projection.applicationId);
  const permissions = useClosingPackagePermissions();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  const {
    filter,
    setFilter,
    readOnlyReason,
    canEdit,
    canInitialize,
    allRows,
    rows,
    emptyDescription,
  } = useCompositionEditor({
    projection,
    composition: composition ?? null,
  });

  const {
    handleRowAction,
    handleManualAddDocument,
    handleUploadedDocuments,
    handleInitializeComposition,
    excludeConfirmModal,
    initializeConfirmModal,
    documentPickerState,
    documentPreviewState,
    actionLoadingRowKey,
    manualAddLoading,
    uploadAddLoading,
    initializeLoading,
  } = useCompositionActionHandlers(projection.applicationId);

  const handleUploadDocument = useCallback(() => {
    setUploadModalOpen(true);
  }, []);

  const linkedCompositionDocumentIds = useMemo(
    () =>
      Array.from(
        new Set(
          allRows
            .filter((row) => row.compositionStatus === 'INCLUDED')
            .map((row) => row.linkedDocumentId)
            .filter((documentId): documentId is number => documentId != null)
        )
      ),
    [allRows]
  );

  const handleAddExistingDocument = useCallback(() => {
    void handleManualAddDocument(linkedCompositionDocumentIds);
  }, [handleManualAddDocument, linkedCompositionDocumentIds]);

  const headerCommands = useMemo(() => {
    const commands: CompositionHeaderCommand[] = [];

    if (canInitialize) {
      commands.push({
        key: 'initialize',
        label: 'Применить рекомендуемый состав',
        onClick: handleInitializeComposition,
        loading: initializeLoading,
        type: 'primary',
        testId: 'composition-initialize',
      });
    }

    return commands;
  }, [canInitialize, handleInitializeComposition, initializeLoading]);

  const toolbarDocumentActions = useMemo((): CompositionToolbarDocumentActions | undefined => {
    if (!canEdit) {
      return undefined;
    }

    return {
      visible: true,
      onAddDocument: handleAddExistingDocument,
      onUploadDocument: handleUploadDocument,
      addDocumentLoading: manualAddLoading,
      uploadDocumentLoading: uploadAddLoading,
      canUploadDocument: permissions.canUpload,
      uploadDisabledReason: UPLOAD_PERMISSION_TOOLTIP,
    };
  }, [
    canEdit,
    handleAddExistingDocument,
    handleUploadDocument,
    manualAddLoading,
    permissions.canUpload,
    uploadAddLoading,
  ]);

  const emptyActions = useMemo(() => {
    if (!canEdit || allRows.length > 0) {
      return null;
    }

    const documentActionLoading = manualAddLoading || uploadAddLoading;
    const uploadDisabled = !permissions.canUpload || documentActionLoading;

    return (
      <Space size={8} wrap className="cp-composition__empty-actions">
        <Tooltip title="Выбрать существующий документ из реестра">
          <Button
            size="small"
            icon={<FolderOpenOutlined />}
            onClick={handleAddExistingDocument}
            loading={manualAddLoading}
            disabled={documentActionLoading}
          >
            Добавить существующий
          </Button>
        </Tooltip>
        <Tooltip
          title={
            permissions.canUpload
              ? 'Загрузить новый документ в реестр'
              : UPLOAD_PERMISSION_TOOLTIP
          }
        >
          <Button
            type="primary"
            size="small"
            icon={<UploadOutlined />}
            onClick={handleUploadDocument}
            loading={uploadAddLoading}
            disabled={uploadDisabled}
          >
            Загрузить документ
          </Button>
        </Tooltip>
      </Space>
    );
  }, [
    allRows.length,
    canEdit,
    handleAddExistingDocument,
    handleUploadDocument,
    manualAddLoading,
    permissions.canUpload,
    uploadAddLoading,
  ]);

  return (
    <div className="cp-composition cp-span-full" data-testid="closing-package-composition">
      <div className="cp-composition__header">
        <CompositionHeader commands={headerCommands} />
      </div>

      <CompositionToolbar
        filter={filter}
        onFilterChange={setFilter}
        documentActions={toolbarDocumentActions}
      />

      <CompositionEditBanner readOnlyReason={readOnlyReason} />

      <CompositionTable
        rows={rows}
        emptyDescription={emptyDescription}
        emptyExtra={emptyActions}
        loading={compositionLoading}
        onRowAction={handleRowAction}
        actionLoadingRowKey={actionLoadingRowKey}
      />

      <CompositionExcludeConfirmModal {...excludeConfirmModal} />
      <CompositionInitializeConfirmModal {...initializeConfirmModal} />
      <DocumentPickerDrawer {...documentPickerState} />
      <DocumentPreviewModal {...documentPreviewState} />
      <UploadDocumentModal
        visible={uploadModalOpen}
        application={{ id: projection.applicationId } as Application}
        onCancel={() => setUploadModalOpen(false)}
        onSuccess={async (documents) =>
          (await handleUploadedDocuments(documents)) === 'success'
        }
        successMessage={false}
      />
    </div>
  );
};

export default ClosingPackageCompositionEditor;
